import { Question, QuestionCore, ModuleId } from '../types'
import { MODULES } from './modules'
import { getQuestionTypeConfig } from '../config/questionTypeRegistry'
import {
  SEED_COLLECTIONS,
  getAllSeedQuestions,
  SeedQuestion,
  getAllQuestionsWithSources as getAllSeedQuestionsWithSources,
} from './seedConsolidator'
import { createRng } from '../utils/deterministicRng'
import { debug, error as logError, group, groupEnd } from '../utils/logger'
function fisherYatesShuffle<T>(array: T[], randomFn: () => number): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(randomFn() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

/**
 * Question Loader — Taxonomy-Authoritative
 *
 * Single source of truth: 8 module-based seed collections.
 * Tier is authoritative.
 * All questions have required taxonomy fields.
 */

// ============================================================
// Seed → QuestionCore Conversion
// ============================================================

/**
 * Convert a SeedQuestion to QuestionCore (the public question type).
 */
function assertCanonicalSeedFeedback(seed: SeedQuestion): void {
  const raw = seed as unknown as Record<string, unknown>
  const nestedFeedback = (raw.feedback ?? {}) as Record<string, unknown>
  const deprecatedFound: string[] = []
  if ('visualAid' in raw) deprecatedFound.push('visualAid')
  if ('explanation' in raw) deprecatedFound.push('explanation')
  if ('explanationPerOption' in raw)
    deprecatedFound.push('explanationPerOption')
  if ('explanationCorrect' in raw) deprecatedFound.push('explanationCorrect')
  if ('distractorExplanations' in raw)
    deprecatedFound.push('distractorExplanations')
  if ('stimulusAids' in nestedFeedback)
    deprecatedFound.push('feedback.stimulusAids')
  if ('supportAids' in nestedFeedback)
    deprecatedFound.push('feedback.supportAids')
  if ('explanation' in nestedFeedback)
    deprecatedFound.push('feedback.explanation')
  if ('explanationPerOption' in nestedFeedback)
    deprecatedFound.push('feedback.explanationPerOption')
  if (deprecatedFound.length > 0) {
    throw new Error(
      `Non-canonical feedback payload for question ${seed.id}. Deprecated keys: ${deprecatedFound.join(', ')}. Supported contract: feedback.explanationText, feedback.solutionSteps, feedback.distractorExplanations, stimulusAids, supportAids.`,
    )
  }
}
function seedToCore(seed: SeedQuestion): QuestionCore {
  assertCanonicalSeedFeedback(seed)
  return {
    id: seed.id,
    module: seed.module,
    question: seed.question,
    questionType: seed.questionType as any,
    citoDifficulty: seed.citoDifficulty,
    citoDiscrimination: seed.citoDiscrimination,
    skill: seed.skill,
    subSkill: seed.subSkill,
    familyId: seed.familyId,
    context: seed.context,
    options: seed.options,
    correctAnswer: seed.correctAnswer,
    interactiveConfig: seed.interactiveConfig,
    isAnchor: seed.isAnchor,
    data: seed.data,
    feedback: seed.feedback,
    stimulusAids: seed.stimulusAids,
    supportAids: seed.supportAids,
    // Taxonomy-authoritative fields (tier is canonical)
    tier: seed.tier,
    levelCode: seed.levelCode,
    group: seed.group,
    conceptId: seed.conceptId,
    variantIndex: seed.variantIndex,
  }
}

// ============================================================
// Build Core Question Arrays
// ============================================================

const ALL_SEEDS = getAllSeedQuestions()

/** All questions as QuestionCore (normalized, without explanations) */
export const ALL_QUESTIONS_CORE: QuestionCore[] = ALL_SEEDS.map(seedToCore)

/**
 * Stable signal that increments when the authoritative question corpus changes.
 * Consumers can depend on this to refresh memoized projections without relying on
 * referential identity of `getAllQuestions()` results.
 */
export const QUESTION_DATA_VERSION = ALL_QUESTIONS_CORE.map(
  (question) => question.id,
).join('|')

/** Explanation data indexed by question ID */
const ALL_EXPLANATIONS: Record<
  string,
  {
    feedback?: Question['feedback']
    stimulusAids?: Question['stimulusAids']
    supportAids?: Question['supportAids']
  }
> = {}
ALL_SEEDS.forEach((seed) => {
  assertCanonicalSeedFeedback(seed)
  const feedback = seed.feedback as Question['feedback']
  const stimulusAids = seed.stimulusAids as Question['stimulusAids']
  const supportAids = seed.supportAids as Question['supportAids']
  if (feedback || stimulusAids || supportAids) {
    ALL_EXPLANATIONS[seed.id] = {
      feedback,
      stimulusAids,
      supportAids,
    }
  }
})

// ============================================================
// CITO Eligibility Check
// ============================================================

/**
 * CITO Eligibility Check.
 */
export function isCitoEligible(question: QuestionCore): boolean {
  const type = question.questionType
  const config = getQuestionTypeConfig(type)
  return config.isCitoEligible && !question.context
}

/**
 * Strict validation — errors for required taxonomy fields.
 */
export function validateQuestion(question: QuestionCore): string[] {
  const errors: string[] = []
  if (!question.questionType) {
    errors.push(`Question ${question.id} is missing required questionType`)
  } else {
    try {
      getQuestionTypeConfig(question.questionType)
    } catch (error) {
      errors.push(
        `Question ${question.id} has invalid canonical questionType: ${question.questionType}`,
      )
    }
  }
  if (!question.familyId) {
    errors.push(`Question ${question.id} is missing required familyId`)
  }
  if (!question.skill) {
    errors.push(`Question ${question.id} is missing required skill`)
  }
  if (!question.subSkill) {
    errors.push(`Question ${question.id} is missing required subSkill`)
  }
  if (!question.tier || question.tier < 1 || question.tier > 16) {
    errors.push(
      `Question ${question.id} has invalid tier: ${question.tier} (valid: 1-16)`,
    )
  }
  if (!question.levelCode) {
    errors.push(`Question ${question.id} is missing required levelCode`)
  }
  if (!question.group || ![6, 7, 8].includes(question.group)) {
    errors.push(`Question ${question.id} has invalid group: ${question.group}`)
  }
  return errors
}

// ============================================================
// Normalization & Validation
// ============================================================

// ============================================================
// PRE-BUILT INDEXES (Performance Optimization)
// ============================================================

/** Lookup a single question by ID */
export const QUESTION_BY_ID: Map<string, QuestionCore> = new Map(
  ALL_QUESTIONS_CORE.map((q) => [q.id, q]),
)

/** All questions for a given module */
export const QUESTIONS_BY_MODULE: Map<ModuleId, QuestionCore[]> = new Map()

/** Questions for a module+subSkill combination */
export const QUESTIONS_BY_MODULE_SUBSKILL: Map<string, QuestionCore[]> =
  new Map()

/** Questions grouped by familyId */
export const QUESTIONS_BY_FAMILY: Map<string, QuestionCore[]> = new Map()

/** All unique subSkills per module */
export const SUBSKILLS_BY_MODULE: Map<ModuleId, Set<string>> = new Map()

/** Questions by module+tier (authoritative index) */
export const QUESTIONS_BY_MODULE_TIER: Map<string, QuestionCore[]> = new Map()

// Build all indexes in a single pass
ALL_QUESTIONS_CORE.forEach((q) => {
  // By module
  if (!QUESTIONS_BY_MODULE.has(q.module)) {
    QUESTIONS_BY_MODULE.set(q.module, [])
  }
  QUESTIONS_BY_MODULE.get(q.module)!.push(q)

  // By module+tier (authoritative)
  const mtKey = `${q.module}-${q.tier}`
  if (!QUESTIONS_BY_MODULE_TIER.has(mtKey)) {
    QUESTIONS_BY_MODULE_TIER.set(mtKey, [])
  }
  QUESTIONS_BY_MODULE_TIER.get(mtKey)!.push(q)

  // By module+subSkill
  const msKey = `${q.module}-${q.subSkill}`
  if (!QUESTIONS_BY_MODULE_SUBSKILL.has(msKey)) {
    QUESTIONS_BY_MODULE_SUBSKILL.set(msKey, [])
  }
  QUESTIONS_BY_MODULE_SUBSKILL.get(msKey)!.push(q)

  // SubSkills per module
  if (!SUBSKILLS_BY_MODULE.has(q.module)) {
    SUBSKILLS_BY_MODULE.set(q.module, new Set())
  }
  SUBSKILLS_BY_MODULE.get(q.module)!.add(q.subSkill)

  // By familyId
  if (!QUESTIONS_BY_FAMILY.has(q.familyId)) {
    QUESTIONS_BY_FAMILY.set(q.familyId, [])
  }
  QUESTIONS_BY_FAMILY.get(q.familyId)!.push(q)
})

// ============================================================
// Index Query Helpers
// ============================================================

/** Get questions for a module (indexed) */
export function getModuleQuestions(moduleId: ModuleId): QuestionCore[] {
  return QUESTIONS_BY_MODULE.get(moduleId) || []
}

/** Get questions for a module+tier (indexed, authoritative) */
export function getModuleTierQuestions(
  moduleId: ModuleId,
  tier: number,
): QuestionCore[] {
  return QUESTIONS_BY_MODULE_TIER.get(`${moduleId}-${tier}`) || []
}

/** Get questions for a module+subSkill (indexed) */
export function getModuleSubSkillQuestions(
  moduleId: ModuleId,
  subSkill: string,
): QuestionCore[] {
  return QUESTIONS_BY_MODULE_SUBSKILL.get(`${moduleId}-${subSkill}`) || []
}

/** Get questions for a module+subSkill+tier (indexed, composed) */
export function getModuleSubSkillTierQuestions(
  moduleId: ModuleId,
  subSkill: string,
  tier: number,
): QuestionCore[] {
  const subSkillQuestions =
    QUESTIONS_BY_MODULE_SUBSKILL.get(`${moduleId}-${subSkill}`) || []
  return subSkillQuestions.filter((q) => q.tier === tier)
}

// CITO-eligible questions subset
export const CITO_ELIGIBLE_QUESTIONS: QuestionCore[] =
  ALL_QUESTIONS_CORE.filter(isCitoEligible)

// ============================================================
// Question Retrieval (with explanations)
// ============================================================

/**
 * Get a single question with its explanation.
 */
export function getQuestionWithExplanation(
  questionId: string,
): Question | null {
  try {
    const core = QUESTION_BY_ID.get(questionId)
    if (!core) {
      logError(`QUESTION ERROR: Question not found: ${questionId}`)
      return null
    }
    const explanation = ALL_EXPLANATIONS[questionId]
    if (!explanation) {
      return {
        ...core,
        feedback: {
          explanationText: 'Uitleg wordt binnenkort toegevoegd.',
          distractorExplanations: {},
        },
      }
    }

    // Return question with explanation data directly
    return {
      ...core,
      feedback: explanation.feedback,
      stimulusAids: explanation.stimulusAids,
      supportAids: explanation.supportAids,
    }
  } catch (error) {
    logError('Error in getQuestionWithExplanation:', error, questionId)
    return null
  }
}

/**
 * Get all questions for a module (with explanations).
 */
export function getQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    const questions = (QUESTIONS_BY_MODULE.get(moduleId) || [])
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
    return questions
  } catch (error) {
    logError('Error in getQuestionsByModule:', error, moduleId)
    return []
  }
}

// ============================================================
// CITO Selection
// ============================================================

/**
 * Get questions for CITO (with intelligent selection).
 *
 * Uses tier-based difficulty distribution for balanced test composition.
 *
 * @param count - Number of questions to select (default: 30)
 * @param seed - Optional seed for deterministic selection (for reproducibility)
 */
export function getCitoQuestions(
  count: number = 30,
  seed?: number,
): QuestionCore[] {
  try {
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(isCitoEligible)
    if (eligible.length === 0) {
      logError('CITO: No eligible questions found')
      return []
    }

    // Create RNG (deterministic if seed provided, random otherwise)
    const rng = seed !== undefined ? createRng(seed) : null
    const randomFn = () => (rng ? rng.random() : Math.random())

    // Group by skill
    const bySkill = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      const skill = q.skill
      if (!bySkill.has(skill)) bySkill.set(skill, [])
      bySkill.get(skill)!.push(q)
    })

    // Within each skill, pick max 1 per familyId
    const selectedBySkill = new Map<string, QuestionCore[]>()
    bySkill.forEach((questions, skill) => {
      const byFamily = new Map<string, QuestionCore[]>()
      questions.forEach((q) => {
        if (!byFamily.has(q.familyId)) byFamily.set(q.familyId, [])
        byFamily.get(q.familyId)!.push(q)
      })
      const skillQuestions: QuestionCore[] = []
      byFamily.forEach((familyQuestions) => {
        const randomIndex = Math.floor(randomFn() * familyQuestions.length)
        skillQuestions.push(familyQuestions[randomIndex])
      })
      selectedBySkill.set(skill, skillQuestions)
    })

    // Proportional allocation per skill
    const totalAvailable = Array.from(selectedBySkill.values()).reduce(
      (sum, questions) => sum + questions.length,
      0,
    )
    const questionsPerSkill = new Map<string, number>()
    selectedBySkill.forEach((questions, skill) => {
      const proportion = questions.length / totalAvailable
      questionsPerSkill.set(skill, Math.max(1, Math.round(count * proportion)))
    })

    // Select with tier-based difficulty balance
    const selected: QuestionCore[] = []
    selectedBySkill.forEach((questions, skill) => {
      const targetCount = questionsPerSkill.get(skill) || 0

      // Use tier for difficulty categorization (1-6 easy, 7-12 medium, 13-16 hard)
      const easyQuestions = questions.filter((q) => q.tier <= 6)
      const mediumQuestions = questions.filter(
        (q) => q.tier >= 7 && q.tier <= 12,
      )
      const hardQuestions = questions.filter((q) => q.tier >= 13)
      const easyCount = Math.round(targetCount * 0.4)
      const mediumCount = Math.round(targetCount * 0.4)
      const hardCount = targetCount - easyCount - mediumCount

      // Deterministic Fisher-Yates shuffle using seeded RNG when available
      const shuffleArray = <T,>(arr: T[]): T[] =>
        fisherYatesShuffle(arr, randomFn)
      const shuffledEasy = shuffleArray(easyQuestions)
      const shuffledMedium = shuffleArray(mediumQuestions)
      const shuffledHard = shuffleArray(hardQuestions)
      selected.push(...shuffledEasy.slice(0, easyCount))
      selected.push(...shuffledMedium.slice(0, mediumCount))
      selected.push(...shuffledHard.slice(0, hardCount))
      const remaining =
        targetCount -
        (Math.min(easyCount, shuffledEasy.length) +
          Math.min(mediumCount, shuffledMedium.length) +
          Math.min(hardCount, shuffledHard.length))
      if (remaining > 0) {
        const allShuffled = shuffleArray(
          questions.filter((q) => !selected.includes(q)),
        )
        selected.push(...allShuffled.slice(0, remaining))
      }
    })
    const finalSelection = shuffleArray(selected).slice(0, count)

    // Shuffle options for each question
    const shuffledQuestions = finalSelection.map((q) =>
      shuffleQuestionOptions(q, seed),
    )
    return shuffledQuestions
  } catch (error) {
    logError('Error in getCitoQuestions:', error)
    return []
  }
}

/**
 * Get CITO questions by skill.
 *
 * @param skill - Skill to filter by
 * @param count - Number of questions to select (default: 10)
 * @param seed - Optional seed for deterministic selection
 */
export function getCitoQuestionsBySkill(
  skill: string,
  count: number = 10,
  seed?: number,
): QuestionCore[] {
  try {
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(
      (q) => isCitoEligible(q) && q.skill === skill,
    )
    if (eligible.length === 0) return []

    // Create RNG (deterministic if seed provided, random otherwise)
    const rng = seed !== undefined ? createRng(seed) : null
    const randomFn = () => (rng ? rng.random() : Math.random())
    const byFamily = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      if (!byFamily.has(q.familyId)) byFamily.set(q.familyId, [])
      byFamily.get(q.familyId)!.push(q)
    })
    const uniqueQuestions: QuestionCore[] = []
    byFamily.forEach((familyQuestions) => {
      const randomIndex = Math.floor(randomFn() * familyQuestions.length)
      uniqueQuestions.push(familyQuestions[randomIndex])
    })

    // Tier-based difficulty balance
    const easyQuestions = uniqueQuestions.filter((q) => q.tier <= 6)
    const mediumQuestions = uniqueQuestions.filter(
      (q) => q.tier >= 7 && q.tier <= 12,
    )
    const hardQuestions = uniqueQuestions.filter((q) => q.tier >= 13)
    const easyCount = Math.round(count * 0.4)
    const mediumCount = Math.round(count * 0.4)
    const hardCount = count - easyCount - mediumCount

    // Deterministic Fisher-Yates shuffle using seeded RNG when available
    const shuffleArray = <T,>(arr: T[]): T[] =>
      fisherYatesShuffle(arr, randomFn)
    const selected: QuestionCore[] = []
    selected.push(...shuffleArray(easyQuestions).slice(0, easyCount))
    selected.push(...shuffleArray(mediumQuestions).slice(0, mediumCount))
    selected.push(...shuffleArray(hardQuestions).slice(0, hardCount))
    const remaining = count - selected.length
    if (remaining > 0) {
      const available = shuffleArray(
        uniqueQuestions.filter((q) => !selected.includes(q)),
      )
      selected.push(...available.slice(0, remaining))
    }
    return shuffleArray(selected).slice(0, count)
  } catch (error) {
    logError('Error in getCitoQuestionsBySkill:', error)
    return []
  }
}

/**
 * Get skill-based performance summary.
 */
export function getCitoSkillSummary(): {
  totalQuestions: number
  bySkill: Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byTier: {
        easy: number
        medium: number
        hard: number
      }
    }
  >
} {
  const bySkill = new Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byTier: {
        easy: number
        medium: number
        hard: number
      }
    }
  >()
  CITO_ELIGIBLE_QUESTIONS.forEach((q) => {
    const skill = q.skill
    if (!bySkill.has(skill)) {
      bySkill.set(skill, {
        total: 0,
        bySubSkill: new Map(),
        byTier: {
          easy: 0,
          medium: 0,
          hard: 0,
        },
      })
    }
    const skillData = bySkill.get(skill)!
    skillData.total++
    // Tier-based categorization
    if (q.tier <= 6) skillData.byTier.easy++
    else if (q.tier <= 12) skillData.byTier.medium++
    else skillData.byTier.hard++
    skillData.bySubSkill.set(
      q.subSkill,
      (skillData.bySubSkill.get(q.subSkill) || 0) + 1,
    )
  })
  return {
    totalQuestions: CITO_ELIGIBLE_QUESTIONS.length,
    bySkill,
  }
}

// ============================================================
// Full Question Retrieval
// ============================================================

/**
 * Get all questions (with explanations).
 */
export function getAllQuestions(): Question[] {
  try {
    return ALL_QUESTIONS_CORE.map((q) =>
      getQuestionWithExplanation(q.id),
    ).filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getAllQuestions:', error)
    return []
  }
}

/**
 * Get questions by type (for special rendering).
 */
export function getQuestionsByType(type: string): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.questionType === type)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsByType:', error)
    return []
  }
}

/**
 * Validate all questions and explanations.
 */
export function validateQuestionsAndExplanations(): {
  valid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []
  ALL_QUESTIONS_CORE.forEach((question) => {
    // Taxonomy validation (strict)
    const taxonomyErrors = validateQuestion(question)
    errors.push(...taxonomyErrors)

    // Content validation
    if (!question.question)
      errors.push(`Question ${question.id} missing question text`)
    if (!question.options || question.options.length === 0) {
      errors.push(`Question ${question.id} missing options`)
    }
    if (
      question.correctAnswer === undefined ||
      question.correctAnswer === null
    ) {
      errors.push(`Question ${question.id} missing correctAnswer`)
    }
    const explanation = ALL_EXPLANATIONS[question.id]
    if (!explanation) {
      warnings.push(`Question ${question.id} missing explanation`)
    }
  })
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// ============================================================
// Tier-Based Retrieval (Authoritative)
// ============================================================

/**
 * Get questions for a specific module and tier.
 */
export function getQuestionsForTier(
  moduleId: ModuleId,
  tier: number,
): Question[] {
  try {
    return (QUESTIONS_BY_MODULE_TIER.get(`${moduleId}-${tier}`) || [])
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsForTier:', error)
    return []
  }
}

/**
 * Get questions for a specific familyId.
 */
export function getQuestionsForFamily(familyId: string): Question[] {
  try {
    return (QUESTIONS_BY_FAMILY.get(familyId) || [])
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsForFamily:', error)
    return []
  }
}

/**
 * Get questions for a familyId within a tier range.
 */
export function getQuestionsForFamilyInTierRange(
  familyId: string,
  minTier: number,
  maxTier: number,
): Question[] {
  try {
    return (QUESTIONS_BY_FAMILY.get(familyId) || [])
      .filter((q) => q.tier >= minTier && q.tier <= maxTier)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsForFamilyInTierRange:', error)
    return []
  }
}

// ============================================================
// Seed Collection Stats
// ============================================================

/**
 * Get statistics about the seed collections.
 */
export function getSeedStats(): {
  totalQuestions: number
  byModule: Record<
    string,
    {
      count: number
      families: number
      tierRange: [number, number]
      questionTypes: string[]
    }
  >
} {
  const byModule: Record<string, any> = {}
  for (const [moduleId, collection] of SEED_COLLECTIONS) {
    const families = new Set<string>()
    const tiers = new Set<number>()
    const types = new Set<string>()
    for (const q of collection.questions) {
      families.add(q.familyId)
      tiers.add(q.tier)
      types.add(q.questionType)
    }
    byModule[moduleId] = {
      count: collection.questions.length,
      families: families.size,
      tierRange:
        tiers.size > 0 ? [Math.min(...tiers), Math.max(...tiers)] : [0, 0],
      questionTypes: Array.from(types).sort(),
    }
  }
  return {
    totalQuestions: ALL_QUESTIONS_CORE.length,
    byModule,
  }
}

// ============================================================
// Option Shuffling
// ============================================================

/**
 * Shuffle question options and update correctAnswer accordingly.
 *
 * @param question - Question to shuffle
 * @param seed - Optional seed for deterministic shuffling
 */
export function shuffleQuestionOptions<T extends QuestionCore>(
  question: T,
  seed?: number,
): T {
  const type = question.questionType
  const config = getQuestionTypeConfig(type)
  if (!config.shouldShuffleOptions) return question

  // Create RNG (deterministic if seed provided, random otherwise)
  const rng =
    seed !== undefined ? createRng(seed + hashQuestionId(question.id)) : null
  const randomFn = () => (rng ? rng.random() : Math.random())
  const indices = fisherYatesShuffle(
    question.options.map((_, i) => i),
    randomFn,
  )
  const shuffledOptions = indices.map((i) => question.options[i])
  const newCorrectAnswer = indices.indexOf(question.correctAnswer)
  return {
    ...question,
    options: shuffledOptions,
    correctAnswer: newCorrectAnswer,
  } as T
}

/**
 * Hash question ID to numeric seed (for option shuffling)
 */
function hashQuestionId(id: string): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0
  }
  return hash >>> 0
}

/**
 * Shuffle options for a full Question (with explanations).
 *
 * @param seed - Optional seed for deterministic shuffling used by governance/QA diagnostics
 */
export function shuffleQuestionWithExplanations(
  question: Question,
  seed?: number,
): Question {
  const type = question.questionType
  const config = getQuestionTypeConfig(type)
  if (!config.shouldShuffleOptions) return question
  const rng =
    seed !== undefined ? createRng(seed + hashQuestionId(question.id)) : null
  const randomFn = () => (rng ? rng.random() : Math.random())
  const indices = fisherYatesShuffle(
    question.options.map((_, i) => i),
    randomFn,
  )
  const shuffledOptions = indices.map((i) => question.options[i])
  const newCorrectAnswer = indices.indexOf(question.correctAnswer)
  let remappedDistractorExplanations: Record<string, string> | undefined
  const currentDistractorExplanations =
    question.feedback?.distractorExplanations
  if (currentDistractorExplanations) {
    remappedDistractorExplanations = {}
    indices.forEach((originalIndex, newIndex) => {
      const originalExplanation =
        currentDistractorExplanations[originalIndex.toString()]
      if (originalExplanation) {
        remappedDistractorExplanations![newIndex.toString()] =
          originalExplanation
      }
    })
  }
  return {
    ...question,
    options: shuffledOptions,
    correctAnswer: newCorrectAnswer,
    feedback: {
      ...question.feedback,
      distractorExplanations: remappedDistractorExplanations,
    },
  }
}

// ============================================================
// Exports
// ============================================================

export { MODULES }

// ============================================================
// Development Logging
// ============================================================

if (process.env.NODE_ENV === 'development') {
  const allErrors = ALL_QUESTIONS_CORE.flatMap(validateQuestion)
  if (allErrors.length > 0) {
    group('❌ Question Taxonomy Validation Errors')
    allErrors.slice(0, 30).forEach((error) => logError(error))
    if (allErrors.length > 30) {
      logError(`... and ${allErrors.length - 30} more errors`)
    }
    groupEnd()
  }
  const stats = getSeedStats()
  debug(
    `✅ Question Loader v4.1: ${stats.totalQuestions} questions across ${Object.keys(stats.byModule).length} modules ` +
      `(${CITO_ELIGIBLE_QUESTIONS.length} CITO-eligible)`,
  )
}
export interface QuestionWithSourceDiagnostics extends QuestionCore {
  questionId: string
  moduleCode: string
  __sourceFile: string
  stimulusAids?: Question['stimulusAids']
  supportAids?: Question['supportAids']
}

/**
 * Returns runtime-loaded questions with provenance for diagnostics.
 */
export function getAllQuestionsWithSourcesForDiagnostics(): QuestionWithSourceDiagnostics[] {
  return getAllSeedQuestionsWithSources().map((seed) => {
    const core = seedToCore(seed)
    return {
      ...core,
      questionId: core.id,
      moduleCode: seed.module
        ? (SEED_COLLECTIONS.get(seed.module)?.moduleCode ?? '')
        : '',
      __sourceFile: seed.__sourceFile ?? 'unknown:source-file-not-available',
      stimulusAids: core.stimulusAids,
      supportAids: core.supportAids,
    }
  })
}
